import { useCallback, useState } from "react";
import { Box, Checkbox, FormControlLabel, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  FormContextData,
  RequestRedoRequest,
  UUID,
} from "@thalamos/aspire-common";
import { getBaseFormTemplate } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { routeFns } from "../../../routePaths.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

export interface RedoReasonOption {
  value: string;
  label: string;
}

// Redo applies before H3 is signed, so unlike s15 amends the reasons are about
// correcting the form before it is relied upon downstream.
const redoReasons: RedoReasonOption[] = [
  { value: "incorrect-patient-details", label: "Patient details incorrect" },
  {
    value: "incorrect-user-details",
    label: "Name, address or email of person completing the form incorrect",
  },
  {
    value: "incorrect-examination-datetime",
    label: "Examination date and/or time incorrect",
  },
  { value: "incorrect-hospital", label: "Hospital details incorrect" },
  { value: "clinical-error", label: "Clinical detail incorrect or incomplete" },
  { value: "other", label: "Other" },
];

/** API functions that can be injected for testing */
export interface RequestRedoDialogApi {
  requestRedo: (
    formId: UUID,
    body: RequestRedoRequest,
  ) => Promise<{ status: number; data?: unknown }>;
  initiateRedo: (
    formId: UUID,
    body: RequestRedoRequest,
  ) => Promise<{ status: number; data?: unknown }>;
}

const defaultApi: RequestRedoDialogApi = {
  requestRedo: (formId, body) => api.forms.requestRedo(formId, body),
  initiateRedo: (formId, body) => api.forms.initiateRedo(formId, body),
};

export function RequestRedoDialog({
  formId,
  formTemplateId,
  onClose,
  reloadFormContext,
  formContext,
  apiOverride,
  onSuccess,
  mode = "request",
  onStarted,
}: {
  formContext: Omit<FormContextData, "patient">;
  formId: UUID;
  formTemplateId: string;
  onClose: () => void;
  reloadFormContext: () => void;
  /** Optional API override for testing */
  apiOverride?: RequestRedoDialogApi;
  /** Optional success callback — defaults to navigating to the success dialog */
  onSuccess?: () => void;
  // "request" asks the form's author to redo it; "start" is the author redoing
  // their own form — request + accept in one step (straight to redo-in-progress).
  mode?: "request" | "start";
  // Called with the new replacement form id after a successful self-initiate, so
  // the caller can open the draft for editing.
  onStarted?: (newFormId: string) => void;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isStart = mode === "start";

  const template = getBaseFormTemplate(formTemplateId);
  const formDescription = template
    ? `Form ${template.formName}: Section ${template.section} - ${template.description}`
    : formTemplateId;

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [additionalInfo, setAdditionalInfo] = useState<string>("");
  const [hasContactedAuthor, setHasContactedAuthor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canContinue =
    !isSubmitting &&
    !!selectedReasons.length &&
    !!additionalInfo.trim() &&
    // Nobody to contact when you are the author redoing your own form.
    (isStart || hasContactedAuthor);

  const sendRedoRequest = useCallback(async () => {
    setIsSubmitting(true);
    const body: RequestRedoRequest = {
      reasons: redoReasons.filter((r) => selectedReasons.includes(r.value)),
      additionalInfo: additionalInfo.trim(),
    };
    try {
      if (isStart) {
        // Self-initiate: request + accept in one step. Goes straight to
        // redo-in-progress and creates the replacement, which we then open.
        const result = await apiClient.initiateRedo(formId, body);
        if (result.status === 200) {
          renderSuccessToast({ message: "Redo started" });
          reloadFormContext();
          const newFormId = (result.data as { newFormId?: string })?.newFormId;
          if (newFormId && onStarted) onStarted(newFormId);
          onClose();
        } else {
          const errorMessage = (result.data as { reason?: string })?.reason;
          throw new Error(errorMessage ?? "Unknown error");
        }
        return;
      }

      const result = await apiClient.requestRedo(formId, body);
      if (result.status === 204) {
        const message = "Redo request successfully sent";
        renderSuccessToast({ message });
        reloadFormContext();
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(
            routeFns.formContextPageSuccessDialog(
              formContext.id,
              formContext.patientId,
              { title: message, message },
            ),
            { replace: true },
          );
        }
      } else {
        const errorMessage = (result.data as { reason?: string })?.reason;
        throw new Error(errorMessage ?? "Unknown error");
      }
    } catch (e) {
      renderErrorToast({ message: (e as Error).message });
      setIsSubmitting(false);
    }
  }, [
    apiClient,
    formId,
    selectedReasons,
    additionalInfo,
    reloadFormContext,
    onSuccess,
    navigate,
    formContext.id,
    formContext.patientId,
    isStart,
    onStarted,
    onClose,
  ]);

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText={isStart ? "Start Redo" : "Request Redo"}
        closeDialog={onClose}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={
            isStart
              ? [
                  t("pages.redoDialog.start.intro", { formDescription }),
                  t("pages.redoDialog.start.originalReplaced"),
                  t("pages.redoDialog.start.considerDeMinimis"),
                ]
              : [
                  t("pages.redoDialog.request.intro", { formDescription }),
                  t("pages.redoDialog.request.originalReplaced"),
                  t("pages.redoDialog.request.considerDeMinimis"),
                ]
          }
          bannerType={BannerList.WARNING}
        />
      </Box>
      <Dropdown
        label="Please select a reason why this form needs to be redone"
        multiple
        name="reason"
        values={redoReasons}
        selectedValue={selectedReasons}
        onChange={(e: string[]) => {
          setSelectedReasons(e);
        }}
      />
      <Box sx={{ mt: 2 }}>
        <TextField
          name="additionalInfo"
          label="Please provide additional information"
          onChange={(e: string) => {
            setAdditionalInfo(e);
          }}
          value={additionalInfo}
          useFullWidth={true}
          multiline
          rows="5"
        />
      </Box>
      {!isStart && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasContactedAuthor}
                onChange={(_, checked) => {
                  setHasContactedAuthor(checked);
                }}
              />
            }
            label={t("pages.redoDialog.contactedAuthorConfirmation")}
          />
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button label={"Close"} onClick={onClose} variant="outlined" />
        <Button
          color="primary"
          disabled={!canContinue}
          label={isStart ? "Start Redo" : "Send Request"}
          onClick={() => {
            void sendRedoRequest();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
